import { useRef, type CSSProperties } from 'react';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { useCVData } from '../../lib/cv-mock-data';
import { WebDialog } from '../common/WebDialog.web';
import { EditCustomizeForm } from './forms/EditCustomizeForm';

interface CustomizeBottomSheetProps {
  visible?: boolean;
  /** Caller-controlled dismiss for inline-modal callers. Defaults to router.back(). */
  onClose?: () => void;
}

export function CustomizeBottomSheet({
  visible = true,
  onClose,
}: CustomizeBottomSheetProps = {}) {
  const { t } = useTranslation('seeker');
  const router = useRouter();
  const { cvConfig, updateCVConfig, flushCVConfigSave } = useCVData();
  const initialConfigRef = useRef(cvConfig);

  const dismiss = onClose ?? (() => router.back());

  const cancel = () => {
    updateCVConfig(initialConfigRef.current);
    flushCVConfigSave();
    dismiss();
  };

  const done = () => dismiss();

  return (
    <WebDialog
      visible={visible}
      title={t('cv.customizeCard.title', { defaultValue: 'Customize Preview' })}
      onClose={done}
      maxWidth={520}
      backdrop="transparent"
      footer={
        <div style={footerStyle}>
          <button type="button" onClick={cancel} style={cancelButtonStyle}>
            {t('cv.edit.customizeCancel')}
          </button>
          <button type="button" onClick={done} style={doneButtonStyle}>
            {t('cv.edit.customizeDone')}
          </button>
        </div>
      }
    >
      <EditCustomizeForm />
    </WebDialog>
  );
}

const footerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
};

const cancelButtonStyle: CSSProperties = {
  minHeight: 44,
  padding: '8px 16px',
  borderRadius: 9999,
  border: 'none',
  background: 'transparent',
  color: 'var(--text-secondary)',
  fontSize: 14,
  fontWeight: 500,
  fontFamily: 'inherit',
  cursor: 'pointer',
};

const doneButtonStyle: CSSProperties = {
  minHeight: 44,
  padding: '10px 20px',
  borderRadius: 9999,
  border: 'none',
  background: 'var(--text-primary)',
  color: 'var(--bg)',
  fontSize: 14,
  fontWeight: 600,
  fontFamily: 'inherit',
  cursor: 'pointer',
};
